import React, { memo } from 'react'
import { SvgIcon, SvgIconProps, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'

export type TProgressLevel = '0' | '25' | '50' | '75' | '100'

export interface IProgressIconProps {
  SvgProps?: SvgIconProps
  /**
   * @example '75'
   * @default '0'
   */
  progress: TProgressLevel
}

const RADIUS = 10
const STROKE_WIDTH = 1.9
const NORMALIZED_RADIUS = RADIUS - STROKE_WIDTH

/**
 * Because `STROKE_WIDTH` is between 1 and 2, there's a slight offset necessary
 * to center this value in <circle />
 */
const NORMALIZED_RADIUS_WITH_OFFSET = NORMALIZED_RADIUS + 1
const CIRCUMFERENCE = NORMALIZED_RADIUS_WITH_OFFSET * 2 * Math.PI

/**
 * This is a result of the `NORMALIZED_RADIUS_WITH_OFFSET`'s additional pixel
 */
const ROTATE_OFFSET = 2

const getPercentage = (progress: string = '0'): number => {
  return parseInt(progress, 10) / 100
}

const ICON_WIDTH = 20

const useProgressIconStyles = makeStyles({
  root: {
    height: ICON_WIDTH,
    width: ICON_WIDTH,
  },
})

export const ProgressIcon = memo<IProgressIconProps>(({ SvgProps = {}, progress = '0' }) => {
  const percentage = getPercentage(progress)
  const strokeDashoffset = CIRCUMFERENCE - percentage * CIRCUMFERENCE
  const classes = useProgressIconStyles()
  return (
    <SvgIcon {...SvgProps} className={classes.root} viewBox={`0 0 ${ICON_WIDTH} ${ICON_WIDTH}`}>
      {/**
       * Circle
       */}
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0ZM2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10Z'
        fill={brandColors.coolGray4}
      />
      {/**
       * The <circle /> element is rotated 90 degrees counter-clockwise because
       * it's not possible to change the starting point of `strokeDasharray` from 3:00 location
       */}
      <g
        transform={`rotate(-90.1 ${NORMALIZED_RADIUS + ROTATE_OFFSET} ${
          NORMALIZED_RADIUS + ROTATE_OFFSET
        })`}>
        <circle
          stroke={brandColors.skyBlue6}
          fill={percentage === 1 ? brandColors.skyBlue6 : 'transparent'}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap='square'
          strokeDasharray={`${CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          style={{ strokeDashoffset }}
          r={NORMALIZED_RADIUS_WITH_OFFSET}
          cx={RADIUS}
          cy={RADIUS}
        />
      </g>
      {/**
       * Check
       */}
      <path
        d='M15.2134 8.0007C15.6004 7.60667 15.5947 6.97353 15.2007 6.58654C14.8067 6.19955 14.1735 6.20525 13.7866 6.59928L8.3125 12.1729L6.21345 10.0356C5.82646 9.64162 5.19332 9.63591 4.79929 10.0229C4.40527 10.4099 4.39956 11.043 4.78655 11.4371L7.59905 14.3007C7.78708 14.4921 8.04416 14.6 8.3125 14.6C8.58084 14.6 8.83792 14.4921 9.02595 14.3007L15.2134 8.0007Z'
        fill={percentage === 1 ? brandColors.white : brandColors.coolGray4}
      />
    </SvgIcon>
  )
})

export default ProgressIcon
